import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createTireMake,
  deleteTireMake,
  getTireMakeById,
  getTireMakesList,
  updateTireMake,
  updateTireMakeStatus,
  type TireMakeListParams,
  type TireMakePayload,
  type TireMakeStatusPayload,
} from "../../services/administrator/tireMakeService";

export const useQueryGetTireMakesList = (
  params: TireMakeListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.tireMakeList, params, language],
      queryFn: () => getTireMakesList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useQueryGetTireMakeById = (
  id?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.tireMakeDetail, id, language],
    queryFn: () => getTireMakeById(id!, language),
    enabled: Boolean(id) && options.enabled !== false,
    refetchOnMount: "always",
    retry: false,
    staleTime: 0,
    ...options,
  });
};

export const useMutationCreateTireMake = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: TireMakePayload) =>
      createTireMake(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tireMakeList,
      }),
  });
};

export const useMutationUpdateTireMake = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: TireMakePayload & { id: string | number };
    }) => updateTireMake(id, payload, language),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.tireMakeList,
        }),
        queryClient.invalidateQueries({
          queryKey: QueryNames.tireMakeDetail,
          refetchType: "none",
        }),
      ]),
  });
};

export const useMutationUpdateTireMakeStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, active }: TireMakeStatusPayload) =>
      updateTireMakeStatus(id, { id, active }, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tireMakeList,
      }),
  });
};

export const useMutationDeleteTireMake = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteTireMake(id, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tireMakeList,
      }),
  });
};
